import { useEffect, useMemo, useRef, useState } from 'react'
import api from '../api/client'
import Boton from './Boton'
import CampoDireccion from './CampoDireccion'
import CampoLocalidad from './CampoLocalidad'
import { PROVINCIAS, esCapitalFederal, matchProvincia } from '../data/argentina'
import { fmtMonto } from '../utils/fecha'
import { colors, text, space, radius } from '../design/tokens'
import { lineasRepartibles, repartoDe, nuevoDestino, destinosParaPayload } from '../utils/envioDestinos'

/**
 * EL cotizador de envío de LGR (NV y presupuestos) — docs/diseno-envio-multiple.md.
 *
 * Dos modos, mismo componente y misma cuenta que la tienda (`POST /envios/cotizar`
 * → EnvioWebService: CABA/GBA por umbral, interior por Andreani + packaging +
 * seguro, tabla de respaldo, regalos por peso aforado):
 *
 *  · UN destino: usa la dirección que le pasa el form (`direccion`) y cotiza
 *    todas las líneas.
 *  · VARIOS destinos (T80): una tarjeta por destino con receptor, dirección y
 *    la cantidad de cada artículo que va ahí. El reparto tiene que ser exacto.
 *    Cada destino se cotiza como una entrega; el envío es la suma.
 *
 * El componente NO agrega la línea: devuelve el costo por `onAgregarLinea` y el
 * form dueño decide cómo insertar el ENV0000 (NV: precio_lista; presupuesto:
 * precio_unitario neto o final según su modo).
 *
 * Estado controlado: `destinos` / `onDestinosChange` (forma interna, ver
 * `nuevoDestino`). Para el wire: `destinosParaPayload` y `destinosDesdeApi`.
 */

export default function CotizadorEnvio({
  lineas = [],            // [{articulo_id, sku, descripcion, cantidad, precio}] del pedido
  direccion = null,       // {provincia, localidad, codigo_postal} para UN destino
  destinos = [],
  onDestinosChange,
  onAgregarLinea,         // ({costo, detalle, articulo_envio_id, multiple}) => void
  inputStyle,
}) {
  const [estado, setEstado] = useState(null)   // null | {loading} | {error} | resultado del endpoint
  const inp = { ...st.input, ...(inputStyle || {}) }

  const repartibles = useMemo(() => lineasRepartibles(lineas), [lineas])
  const multiple = destinos.length > 0

  // Reparto: asignado vs pedido por SKU (solo en modo múltiple).
  const reparto = useMemo(() => repartoDe(destinos, lineas), [destinos, lineas])

  // Si cambian los insumos (renglones, precios, dirección simple), la
  // cotización que se ve deja de valer: se limpia, y las de los destinos también.
  const firmaInsumos = JSON.stringify([repartibles.map(l => [l.sku, l.cantidad, Math.round((l.precio || 0) * 100)]), direccion?.provincia, direccion?.localidad, direccion?.codigo_postal])
  const firmaPrev = useRef(firmaInsumos)
  useEffect(() => {
    if (firmaPrev.current === firmaInsumos) return
    firmaPrev.current = firmaInsumos
    setEstado(null)
    if (destinos.some(d => d.cotizacion)) onDestinosChange?.(destinos.map(d => ({ ...d, cotizacion: null })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaInsumos])

  // Guardia de carrera: solo la última cotización pedida puede escribir; y
  // los destinos se leen del ref (lo que el operador tiene AHORA, no lo capturado).
  const seq = useRef(0)
  const destinosRef = useRef(destinos)
  useEffect(() => { destinosRef.current = destinos }, [destinos])

  const cambiar = (destinosNuevos) => { setEstado(null); onDestinosChange?.(destinosNuevos) }
  const setDestino = (key, cambios) => cambiar(destinos.map(d => d._key === key ? { ...d, ...cambios, cotizacion: null } : d))
  const setItem = (key, sku, valor) => cambiar(destinos.map(d => d._key === key ? { ...d, items: { ...d.items, [sku]: valor }, cotizacion: null } : d))
  const restoAca = (key, sku) => {
    const l = repartibles.find(r => r.sku === sku)
    const otros = destinos.filter(d => d._key !== key).reduce((a, d) => a + (parseFloat(d.items?.[sku]) || 0), 0)
    setItem(key, sku, Math.max(0, (l?.cantidad || 0) - otros))
  }

  const activarMultiple = () => cambiar([nuevoDestino(), nuevoDestino()])
  const volverASimple = () => cambiar([])

  const cotizar = async () => {
    if (repartibles.length === 0) { setEstado({ error: 'Agregá artículos al pedido antes de cotizar el envío.' }); return }
    let payload
    if (multiple) {
      if (!reparto.exacto) { setEstado({ error: 'El reparto no cierra: cada artículo tiene que quedar asignado exactamente entre los destinos.' }); return }
      const sinProvincia = destinos.findIndex(d => !d.provincia?.trim())
      if (sinProvincia >= 0) { setEstado({ error: `Al destino ${sinProvincia + 1} le falta la provincia.` }); return }
      payload = { destinos: destinosParaPayload(destinos, repartibles).map(d => ({ ...d, cotizacion: undefined })) }
    } else {
      if (!direccion?.provincia && !direccion?.codigo_postal) { setEstado({ error: 'Falta la dirección de entrega (provincia y CP) para cotizar.' }); return }
      payload = { destinos: [{
        provincia: direccion.provincia || null, localidad: direccion.localidad || null, codigo_postal: direccion.codigo_postal || null,
        items: repartibles.map(l => ({ articulo_id: l.articulo_id ?? null, sku: l.sku, cantidad: l.cantidad, precio: l.precio ?? null })),
      }] }
    }
    const mio = ++seq.current
    const claves = destinos.map(d => d._key)
    setEstado({ loading: true })
    try {
      const { data } = await api.post('/envios/cotizar', payload)
      if (mio !== seq.current) return   // llegó tarde: hay otra cotización en curso
      setEstado(data)
      if (multiple) {
        // Cada cotización va al destino que la pidió (por clave), sobre el estado actual.
        const porClave = Object.fromEntries(claves.map((k, i) => [k, data.destinos?.[i] || null]))
        onDestinosChange?.(destinosRef.current.map(d => (d._key in porClave ? { ...d, cotizacion: porClave[d._key] } : d)))
      }
    } catch (err) {
      if (mio !== seq.current) return
      setEstado({ error: err.response?.data?.message || 'No se pudo cotizar el envío.' })
    }
  }

  const agregar = () => {
    if (!estado || estado.pendiente || estado.costo == null) return
    const n = destinos.length
    onAgregarLinea?.({
      costo: Number(estado.costo),
      detalle: multiple ? `ENVÍO MÚLTIPLE (${n} destinos)` : `ENVÍO A DOMICILIO (${estado.destinos?.[0]?.label || 'envío'})`,
      articulo_envio_id: estado.articulo_envio_id,
      multiple,
    })
  }

  return (
    <div style={st.wrap}>
      <div style={st.head}>
        <span style={st.titulo}>🚚 Envío</span>
        {multiple
          ? <Boton variante="ghost" sm type="button" onClick={volverASimple}>← Un solo destino</Boton>
          : <Boton variante="ghost" sm type="button" onClick={activarMultiple}>Varios destinos (T80)</Boton>}
      </div>

      {multiple && (
        <>
          <div style={{ ...st.nota, ...(reparto.exacto ? {} : st.notaAlerta) }}>
            Reparto: <b>{reparto.asignado}</b> de <b>{reparto.pedido}</b> unidades asignadas
            {reparto.exacto ? ' ✓' : ' — cada artículo tiene que quedar repartido exacto entre los destinos.'}
            {' '}Con varios destinos, la dirección de entrega de arriba no se usa.
          </div>
          {destinos.map((d, i) => (
            <DestinoCard key={d._key} idx={i} d={d} repartibles={repartibles} inp={inp}
              onChange={c => setDestino(d._key, c)} onItem={(sku, v) => setItem(d._key, sku, v)} onResto={sku => restoAca(d._key, sku)}
              onQuitar={destinos.length > 1 ? () => cambiar(destinos.filter(x => x._key !== d._key)) : null} />
          ))}
          <Boton variante="secondary" sm type="button" onClick={() => cambiar([...destinos, nuevoDestino()])}>+ Agregar destino</Boton>
        </>
      )}

      <div style={{ marginTop: space.sm, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Boton variante="secondary" sm type="button" onClick={cotizar} disabled={estado?.loading}>
          {estado?.loading ? 'Cotizando…' : (multiple ? `Cotizar ${destinos.length} destinos` : 'Cotizar envío')}
        </Boton>
        {estado?.error && <span style={{ fontSize: text.sec, color: colors.err }}>{estado.error}</span>}
      </div>

      {estado && !estado.loading && !estado.error && (
        <div style={st.cotBox}>
          {!multiple && estado.destinos?.[0] && <Cotizacion q={estado.destinos[0]} />}
          {estado.pendiente ? (
            <div style={{ color: colors.warn, fontWeight: 600 }}>⚠ {estado.mensaje}</div>
          ) : (
            <div style={{ marginTop: 4, fontSize: text.body }}>
              Envío a cobrar{multiple ? ` (${destinos.length} destinos)` : ''}: <b>{estado.gratis ? 'sin cargo' : `$${fmtMonto(estado.costo)}`}</b>
            </div>
          )}
          {!estado.pendiente && estado.costo > 0 && estado.articulo_envio_id && (
            <div style={{ marginTop: 8 }}>
              <Boton variante="primary" sm type="button" onClick={agregar}>➕ Agregar como línea de envío (ENV0000)</Boton>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function DestinoCard({ idx, d, repartibles, inp, onChange, onItem, onResto, onQuitar }) {
  const provinciaCanon = matchProvincia(d.provincia)
  const esCaba = esCapitalFederal(provinciaCanon)
  return (
    <div style={st.destino}>
      <div style={st.destinoHead}>
        <b>Destino {idx + 1}</b>
        {d.cotizacion && <Cotizacion q={d.cotizacion} compacta />}
        {onQuitar && <Boton variante="ghost" sm type="button" onClick={onQuitar} style={{ marginLeft: 'auto' }}>✕</Boton>}
      </div>
      <div style={st.row2}>
        <div><label style={st.label}>Recibe</label><input style={inp} value={d.receptor_nombre} onChange={e => onChange({ receptor_nombre: e.target.value })} placeholder="Nombre de quien recibe" /></div>
        <div><label style={st.label}>Teléfono</label><input style={inp} value={d.telefono} onChange={e => onChange({ telefono: e.target.value })} placeholder="+54 11 …" /></div>
      </div>
      <div style={st.row2}>
        <div>
          <label style={st.label}>Domicilio (calle y número)</label>
          <CampoDireccion value={d.calle} onChange={v => onChange({ calle: v })} inputStyle={inp} placeholder="Calle y número"
            onPlaceSelected={p => onChange({
              calle: p.calle || d.calle,
              piso: d.piso || p.detalle_extra || '',
              localidad: p.localidad || d.localidad,
              provincia: p.provincia || d.provincia,
              codigo_postal: p.codigo_postal || d.codigo_postal,
            })} />
        </div>
        <div><label style={st.label}>Piso / Depto</label><input style={inp} value={d.piso} onChange={e => onChange({ piso: e.target.value })} placeholder="Ej: 6° B, timbre…" /></div>
      </div>
      <div style={st.row3}>
        <div>
          <label style={st.label}>Provincia</label>
          <select style={inp} value={provinciaCanon} onChange={e => onChange({ provincia: e.target.value, localidad: esCapitalFederal(e.target.value) ? '' : d.localidad })}>
            <option value="">— Provincia —</option>
            {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        {!esCaba && (
          <div><label style={st.label}>Localidad</label><CampoLocalidad provincia={provinciaCanon} value={d.localidad} onChange={v => onChange({ localidad: v })} inputStyle={inp} cp={d.codigo_postal} /></div>
        )}
        <div><label style={st.label}>Código postal{esCaba ? ' *' : ''}</label><input style={inp} value={d.codigo_postal} onChange={e => onChange({ codigo_postal: e.target.value })} /></div>
      </div>
      <div><label style={st.label}>Notas para la entrega</label><input style={inp} value={d.notas} onChange={e => onChange({ notas: e.target.value })} placeholder="Horario, referencias…" /></div>

      <table style={st.tabla}>
        <tbody>
          {repartibles.map(l => (
            <tr key={l.sku}>
              <td style={st.td}><span style={st.mono}>{l.sku}</span> {l.descripcion}</td>
              <td style={{ ...st.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                <input type="number" min="0" step="1" style={{ ...inp, width: 64, textAlign: 'right' }} value={d.items?.[l.sku] ?? ''} onChange={e => onItem(l.sku, e.target.value)} />
                <span style={st.meta}> / {l.cantidad}</span>
                <button type="button" style={st.link} onClick={() => onResto(l.sku)} title="Poner acá lo que falta repartir">resto</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Cotizacion({ q, compacta = false }) {
  if (!q) return null
  const costo = q.pendiente ? '⚠ a cotizar aparte' : (q.gratis ? 'sin cargo' : `$${fmtMonto(q.costo)}`)
  if (compacta) return <span style={{ ...st.meta, color: q.pendiente ? colors.warn : colors.textSub }}>{q.label || ''} · {costo}</span>
  return (
    <div>
      <div>{q.label}{q.mensaje ? ` — ${q.mensaje}` : ''}</div>
      {q.desglose && !q.desglose.tabla && (
        <>
          <div>Peso estimado: <b>{q.desglose.peso_kg} kg</b></div>
          <div>Tarifa Andreani (c/IVA): <b>${fmtMonto(q.desglose.andreani_con_iva)}</b> · × {q.desglose.packaging_factor} packaging = <b>${fmtMonto(q.desglose.con_packaging)}</b></div>
          <div>Seguro Andreani ({q.desglose.seguro_pct}% de ${fmtMonto(q.desglose.valor_declarado)} declarados, con IVA): <b>${fmtMonto(q.desglose.seguro)}</b></div>
        </>
      )}
      {q.desglose?.tabla && <div>Peso estimado: <b>{q.desglose.peso_kg} kg</b> · tarifa de la tabla por peso (Andreani no respondió).</div>}
    </div>
  )
}

const st = {
  wrap: { marginTop: space.sm },
  head: { display: 'flex', alignItems: 'center', gap: 8 },
  titulo: { fontWeight: 600, fontSize: text.body, color: colors.text },
  nota: { marginTop: 6, fontSize: text.sec, color: colors.textSub },
  notaAlerta: { color: colors.warn },
  destino: { marginTop: space.sm, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '10px 12px', background: colors.bg },
  destinoHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, fontSize: text.body },
  row2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: space.sm, marginBottom: 6 },
  row3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: space.sm, marginBottom: 6 },
  label: { display: 'block', fontSize: text.meta, color: colors.textMeta, marginBottom: 2 },
  input: { width: '100%', padding: '6px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: text.sec, boxSizing: 'border-box' },
  tabla: { width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: text.sec },
  td: { padding: '3px 4px', borderTop: `1px solid ${colors.border}` },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: text.meta, color: colors.textMeta },
  meta: { fontSize: text.meta, color: colors.textMeta },
  link: { marginLeft: 6, background: 'none', border: 'none', color: colors.link, cursor: 'pointer', fontSize: text.meta, padding: 0 },
  cotBox: { marginTop: 8, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '10px 12px', fontSize: text.sec, color: colors.textSub },
}
